import React, { useState } from 'react';
import { usePushSubscription } from '../hooks/usePushSubscription';

/**
 * PushPermissionBanner
 * ---------------------
 * Gentle one-time prompt at the top of the dashboard. Renders nothing
 * when push is unsupported, already granted, or the user has denied/
 * dismissed. We persist the dismissal in localStorage so we don't nag.
 */

const DISMISS_KEY = 'wizkids:push-banner-dismissed';

export const PushPermissionBanner = () => {
  const { state, error, subscribe } = usePushSubscription();
  const [dismissed, setDismissed] = useState(
    typeof window !== 'undefined' && window.localStorage.getItem(DISMISS_KEY) === '1'
  );

  // Don't render when:
  //  • not supported (iOS Safari without home-screen install)
  //  • permission already granted (we're subscribed silently)
  //  • user has denied (browser-level — only resettable in OS settings)
  //  • user dismissed our banner
  if (state === 'unsupported' || state === 'granted' || state === 'denied' || dismissed) {
    return null;
  }
  if (state === 'idle') return null; // still figuring it out

  const dismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      data-testid="push-banner"
      style={{
        background: 'hsl(220 80% 95%)',
        color: 'hsl(220 60% 25%)',
        border: '1px solid hsl(220 60% 80%)',
        borderRadius: 6,
        padding: '0.6rem 0.9rem',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: 1, minWidth: 220, fontSize: '0.9rem' }}>
        <strong>🔔 Get notified</strong> — turn on notifications so you see new messages from your childcare provider instantly.
        {state === 'error' && error && (
          <div style={{ color: 'hsl(0 70% 35%)', fontSize: '0.78rem', marginTop: 4 }}>
            {error}
          </div>
        )}
      </div>
      <button
        type="button"
        className="btn btn-primary"
        style={{ padding: '0.35rem 0.85rem', fontSize: '0.85rem' }}
        onClick={subscribe}
        data-testid="push-banner-enable"
      >
        Enable
      </button>
      <button
        type="button"
        className="btn btn-ghost"
        style={{ padding: '0.35rem 0.7rem', fontSize: '0.85rem' }}
        onClick={dismiss}
        data-testid="push-banner-dismiss"
      >
        Not now
      </button>
    </div>
  );
};

export default PushPermissionBanner;
